import { Dialog, DialogContent } from '@/components/atoms/dialog';

import { useAssociationManager } from './use-association-manager';
import { SelectGroupStep } from './SelectGroupStep';
import { CreateGroupStep } from './CreateGroupStep';
import { EditGroupStep } from './EditGroupStep';
import { SelectProductsStep } from './SelectProductsStep';
import { ConfigureVariantsStep } from './ConfigureVariantsStep';
import type { AssociateProductsManagerProps } from './types';

export function AssociateProductsManager({
  open,
  onOpenChange,
  currentProduct,
  shopId,
}: AssociateProductsManagerProps) {
  const {
    state,
    actions,
    data,
    loading,
    mutations,
  } = useAssociationManager({
    currentProduct,
    shopId,
    onClose: () => onOpenChange(false),
  });

  const variationType = state.selectedGroup?.variationType || state.newGroupVariationType;

  return (
    <Dialog open={open} onOpenChange={actions.handleClose}>
      <DialogContent className="max-w-2xl">
        {state.step === 'select_group' && (
          <SelectGroupStep
            groups={data.groups}
            isLoadingGroups={loading.isLoadingGroups}
            groupToDelete={state.groupToDelete}
            isDeleting={mutations.deleteGroup.isPending}
            variationType={variationType}
            onSelectGroup={actions.handleSelectGroup}
            onCreateNewGroup={actions.handleCreateNewGroup}
            onEditGroup={actions.handleEditGroup}
            onDeleteGroup={actions.handleDeleteGroup}
            onSetGroupToDelete={actions.setGroupToDelete}
            onClose={actions.handleClose}
          />
        )}

        {state.step === 'create_group' && (
          <CreateGroupStep
            newGroupName={state.newGroupName}
            newGroupDescription={state.newGroupDescription}
            newGroupVariationType={state.newGroupVariationType}
            isCreating={mutations.createGroup.isPending}
            variationType={variationType}
            onSetNewGroupName={actions.setNewGroupName}
            onSetNewGroupDescription={actions.setNewGroupDescription}
            onSetNewGroupVariationType={actions.setNewGroupVariationType}
            onBack={actions.handleBackToGroups}
            onCreate={actions.handleCreateGroup}
          />
        )}

        {state.step === 'edit_group' && (
          <EditGroupStep
            editGroupName={state.editGroupName}
            editGroupDescription={state.editGroupDescription}
            groupToEdit={state.groupToEdit}
            isUpdating={mutations.updateGroup.isPending}
            variationType={variationType}
            onSetEditGroupName={actions.setEditGroupName}
            onSetEditGroupDescription={actions.setEditGroupDescription}
            onBack={actions.handleBackToGroups}
            onSave={actions.handleSaveEditGroup}
          />
        )}

        {state.step === 'select_products' && (
          <SelectProductsStep
            selectedGroup={state.selectedGroup}
            searchQuery={state.searchQuery}
            selectedProductIds={state.selectedProductIds}
            availableProducts={data.availableProducts}
            isLoadingProducts={loading.isLoadingProducts}
            isAddingProducts={mutations.addProducts.isPending}
            variationType={variationType}
            onSetSearchQuery={actions.setSearchQuery}
            onToggleProduct={actions.handleToggleProduct}
            onBack={actions.handleBackToGroups}
            onContinue={actions.handleContinueToVariants}
          />
        )}

        {state.step === 'configure_variants' && (
          <ConfigureVariantsStep
            currentProduct={currentProduct}
            currentProductId={data.currentProductId}
            selectedProductIds={state.selectedProductIds}
            availableProducts={data.availableProducts}
            variantConfigs={state.variantConfigs}
            isUpdating={mutations.updateGroup.isPending}
            variationType={variationType}
            onUpdateVariantConfig={actions.handleUpdateVariantConfig}
            onBack={actions.handleBackToProducts}
            onSubmit={actions.handleSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export type { AssociateProductsManagerProps } from './types';
